'use client';
import React, { useEffect, useRef, useState } from 'react';

import {
  getDocumentImageKey,
  getDocumentImageProxyUrl,
} from '@/services/util/document-image-resolver';
import { normalizeDocumentImageUrl } from '@/services/util/document-image-url';

interface Props {
  src: string;
  alt?: string;
  title?: string;
  className?: string;
  widthAttr?: string;
  alignAttr?: string;
  draggable?: boolean;
}

type ResolveStatus = 'loading' | 'loaded' | 'error';

export default function DocumentImageResolved({
  src,
  alt = '',
  title,
  className,
  widthAttr,
  alignAttr,
  draggable,
}: Props) {
  const imageKey = getDocumentImageKey(src);
  const isKey = imageKey !== null;
  const [status, setStatus] = useState<ResolveStatus>(isKey ? 'loading' : 'loaded');
  const [retryNonce, setRetryNonce] = useState(0);
  const autoRefreshedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    autoRefreshedKeyRef.current = null;
    setRetryNonce(0);
    setStatus(isKey ? 'loading' : 'loaded');
  }, [isKey, src]);

  const handleRetry = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    autoRefreshedKeyRef.current = null;
    setStatus(isKey ? 'loading' : 'loaded');
    setRetryNonce(Date.now());
  };

  const imageUrl = imageKey
    ? getDocumentImageProxyUrl(imageKey, retryNonce)
    : normalizeDocumentImageUrl(src);

  if (status === 'error') {
    return (
      <div
        className={`assistiv-image-placeholder assistiv-image-placeholder--error ${className ?? ''}`}
        data-width={widthAttr}
        data-align={alignAttr}
      >
        <span>Image failed to load</span>
        <button type='button' onClick={handleRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div
        className={`assistiv-image-placeholder assistiv-image-placeholder--error ${className ?? ''}`}
        data-width={widthAttr}
        data-align={alignAttr}
      >
        <span>Image failed to load</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      title={title}
      className={`${className ?? ''}${status === 'loading' ? ' opacity-60' : ''}`}
      data-width={widthAttr}
      data-align={alignAttr}
      draggable={draggable}
      onLoad={() => {
        if (imageKey) autoRefreshedKeyRef.current = null;
        setStatus('loaded');
      }}
      onError={() => {
        if (imageKey && autoRefreshedKeyRef.current !== imageKey) {
          autoRefreshedKeyRef.current = imageKey;
          setStatus('loading');
          setRetryNonce(Date.now());
          return;
        }
        setStatus('error');
      }}
    />
  );
}
